import { isValid, parse } from 'date-fns';
import { ValidateResult } from './ValidateResult';
import ASSET_TYPES from '../types/AssetTypes';

/**
 * @typedef {import('../types/ContentItems').ContentItem } ContentItem
 * @typedef {import('../types/CampaignSchedule').CampaignScheduleEntry } CampaignScheduleEntry
 * @typedef {import('../types/ScheduleValue').ScheduleValue } ScheduleValue
 */

/**
 * @type {Object.<string, string>}
 * Enumeration of possible return codes for validation results.
 */
export const RETURN_CODES = Object.freeze({
  OK: 'OK',
  INVALID_DATA: 'INVALID_DATA',
  EMPTY: 'EMPTY',
  TOO_SHORT: 'TOO_SHORT',
  TOO_LONG: 'TOO_LONG',
  NOT_BETWEEN: 'NOT_BETWEEN',
  DUPLICATE_ASSET: 'DUPLICATE_ASSET',
  DUPLICATE_VALUE: 'DUPLICATE_VALUE',
});

const ASSET_MAX_DURATION_MS = 3600000; // 1 hour

export const Validators = {
  // Dummy -------------------------------------------------------------------------------
  /**
   * Returns a successful validation result.
   * @returns {ValidateResult} A successful validation result.
   */
  successValidate: name =>
    new ValidateResult(true, `${name} validation successful.`, RETURN_CODES.OK),

  // Strings -------------------------------------------------------------------------------
  /**
   * Validates that a value is a string.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {*} value - The value to validate.
   * @returns {ValidateResult} The result of the validation.
   */
  isString: (name, value) => {
    const valid = typeof value === 'string';

    return new ValidateResult(
      valid,
      `${name} Value should be a string`,
      valid ? RETURN_CODES.OK : RETURN_CODES.INVALID_DATA,
    );
  },
  /**
   * Validates that a string's length is between specified minimum and maximum lengths.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {*} value - The string value to validate.
   * @param {number|null} minLength - The minimum allowed length (or null for no minimum).
   * @param {number|null} maxLength - The maximum allowed length (or null for no maximum).
   * @returns {ValidateResult} The result of the validation.
   */
  stringBetweenLengths: (name, value, minLength, maxLength) => {
    if (typeof value !== 'string') {
      return new ValidateResult(
        false,
        `${name} Value should be a string`,
        RETURN_CODES.INVALID_DATA,
      );
    }

    const length = value.length;

    if (minLength === null && maxLength === null) {
      return Validators.successValidate(name);
    }

    if (minLength === null) {
      const isValid = length <= maxLength;

      return new ValidateResult(
        isValid,
        `${name} length must be at most ${maxLength} characters.`,
        isValid ? RETURN_CODES.OK : RETURN_CODES.TOO_LONG,
      );
    }

    if (maxLength === null) {
      const isValid = length >= minLength;

      return new ValidateResult(
        isValid,
        `${name} length must be at least ${minLength} characters.`,
        isValid ? RETURN_CODES.OK : RETURN_CODES.TOO_SHORT,
      );
    }

    const isValid = length >= minLength && length <= maxLength;

    return new ValidateResult(
      isValid,
      `${name} length must be between ${minLength} and ${maxLength} characters.`,
      isValid ? RETURN_CODES.OK : RETURN_CODES.NOT_BETWEEN,
    );
  },

  // Numbers -------------------------------------------------------------------------------
  /**
   * Validates that a value is a number.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {*} value - The value to validate as a number.
   * @returns {ValidateResult} The result of the validation.
   */
  isNumber: (name, value) => {
    const n = Number(value);
    // Number() coerces "", null, and booleans to 0/1. These should usually be invalid for strict number fields.
    const valid = !Number.isNaN(n) && value !== '' && value !== null && typeof value !== 'boolean';

    return new ValidateResult(
      valid,
      `${name} Value should be a number`,
      valid ? RETURN_CODES.OK : RETURN_CODES.INVALID_DATA,
    );
  },
  /**
   * Validates that a value is an integer.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {*} value - The value to validate as an integer.
   * @returns {ValidateResult} The result of the validation.
   */
  isInteger: (name, value) => {
    const isNumber = Validators.isNumber(name, value);

    if (!isNumber.isValid) {
      return isNumber;
    }

    const valid = Number.isInteger(Number(value));

    return new ValidateResult(
      valid,
      `${name} should be an integer`,
      valid ? RETURN_CODES.OK : RETURN_CODES.INVALID_DATA,
    );
  },
  /**
   * Validates that a number is within a specified range.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {*} value - The value to validate as a number.
   * @param {number|null} min - The minimum allowed value (or null for no minimum).
   * @param {number|null} max - The maximum allowed value (or null for no maximum).
   * @returns {ValidateResult} The result of the validation.
   */
  numberInRange: (name, value, min, max) => {
    const isInteger = Validators.isInteger(name, value);

    if (!isInteger.isValid) {
      return isInteger;
    }

    const n = Number(value);

    if (min === null && max === null) {
      return Validators.successValidate(name);
    }

    if (min === null) {
      const isValid = n <= max;

      return new ValidateResult(
        isValid,
        `${name} must be at most ${max}.`,
        isValid ? RETURN_CODES.OK : RETURN_CODES.TOO_LONG,
      );
    }

    if (max === null) {
      const isValid = n >= min;

      return new ValidateResult(
        isValid,
        `${name} must be at least ${min}.`,
        isValid ? RETURN_CODES.OK : RETURN_CODES.TOO_SHORT,
      );
    }

    const isValid = n >= min && n <= max;

    return new ValidateResult(
      isValid,
      `${name} must be between ${min} and ${max}.`,
      isValid ? RETURN_CODES.OK : RETURN_CODES.NOT_BETWEEN,
    );
  },

  // Object -------------------------------------------------------------------------------
  /**
   * Validates that an object contains the specified properties.
   * @param {string} name - The name of the object being validated (for error messages).
   * @param {*} obj - The object to validate.
   * @param {string[]} properties - The list of required property names.
   * @returns {ValidateResult} The result of the validation
   */
  propertiesExist: (name, obj, properties) => {
    if (typeof obj !== 'object' || obj === null) {
      return new ValidateResult(false, `${name} is not a valid object.`, RETURN_CODES.INVALID_DATA);
    }

    for (const prop of properties) {
      if (!(prop in obj)) {
        return new ValidateResult(
          false,
          `${name} is missing required property: ${prop}.`,
          RETURN_CODES.INVALID_DATA,
        );
      }
    }

    return Validators.successValidate(name);
  },

  /**
   * Validate an asset object
   * @param {ContentItem} asset - Asset object to validate
   * @param {string|null} lastAddedAssetId - The ID of the last added asset, if any.
   * @param {string|null} lastAddedAssetType - The type of the last added asset, if any.
   * @returns {ValidateResult} The result of the validation.
   */
  validateAsset: (asset, lastAddedAssetId = null, lastAddedAssetType = null) => {
    if (!asset?.id) {
      return new ValidateResult(false, 'Invalid asset provided.', RETURN_CODES.INVALID_DATA);
    }

    if (asset.id === lastAddedAssetId && asset.type === lastAddedAssetType) {
      return new ValidateResult(
        false,
        'This asset has already been added.',
        RETURN_CODES.DUPLICATE_ASSET,
      );
    }

    if (!asset?.type || !Object.values(ASSET_TYPES).includes(asset.type)) {
      return new ValidateResult(
        false,
        `Asset type is missing or invalid. (${asset?.type})`,
        RETURN_CODES.INVALID_DATA,
      );
    }

    if (!asset?.width || asset.width <= 0) {
      return new ValidateResult(
        false,
        'Asset width is missing or invalid.',
        RETURN_CODES.INVALID_DATA,
      );
    }

    if (!asset?.height || asset.height <= 0) {
      return new ValidateResult(
        false,
        'Asset height is missing or invalid.',
        RETURN_CODES.INVALID_DATA,
      );
    }

    if (!asset?.duration || asset.duration <= 0) {
      return new ValidateResult(
        false,
        'Asset duration must be greater than zero.',
        RETURN_CODES.TOO_SHORT,
      );
    }

    if (asset.duration > ASSET_MAX_DURATION_MS) {
      return new ValidateResult(
        false,
        'Asset duration must be less than or equal to one hour.',
        RETURN_CODES.TOO_LONG,
      );
    }

    return new ValidateResult(true, 'Asset validation passed.', RETURN_CODES.OK);
  },

  /**
   * Validates that a value is not empty.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {*} value - The value to validate.
   * @returns {ValidateResult} The result of the validation.
   */
  isNotEmpty: (name, value) => {
    switch (true) {
      case value === undefined:
      case value === null:
      case value === false:
      case typeof value === 'string' && value.length === 0:
      case Array.isArray(value) && value.length === 0:
      case typeof value === 'object' && Object.keys(value).length === 0:
        return new ValidateResult(false, `${name} is empty.`, RETURN_CODES.EMPTY);
      default:
        return new ValidateResult(true, `${name} is not empty.`, RETURN_CODES.OK);
    }
  },

  /**
   * Validates a campaign schedule object.
   * @param {CampaignScheduleEntry} campaignSchedule - The campaign schedule to validate.
   * @returns {ValidateResult} The result of the validation.
   */
  validateCampaignSchedule: campaignSchedule => {
    const empty = Validators.isNotEmpty('Campaign Schedule', campaignSchedule);

    if (!empty.isValid) {
      return empty;
    }

    const hasId = Validators.isInteger('Campaign Schedule ID', campaignSchedule.id);

    if (!hasId.isValid) {
      return hasId;
    }

    const hasCampaignId = Validators.isInteger(
      'Campaign Schedule Campaign ID',
      campaignSchedule.campaignId,
    );

    if (!hasCampaignId.isValid) {
      return hasCampaignId;
    }

    const hasCampaignName = Validators.isString(
      'Campaign Schedule Campaign Name',
      campaignSchedule.campaignName,
    );

    if (!hasCampaignName.isValid) {
      return hasCampaignName;
    }

    return Validators.successValidate('Campaign Schedule');
  },

  /**
   * Validates that a value is unique within a given array.
   * @param {string} name - The name of the field being validated (for error messages).
   * @param {string} value - The value to check for uniqueness.
   * @param {string[]} allItems - The array of existing items to check against.
   * @param {boolean} [ignoreCase=false] - Whether to ignore case when checking for uniqueness.
   * @returns {ValidateResult} The result of the validation.
   */
  isUnique(name, value, allItems, ignoreCase = false) {
    const itemToCheck = ignoreCase ? value.toLowerCase() : value;
    const arrayToCheckAgainst = allItems.map(item => {
      return ignoreCase ? item.toLowerCase() : item;
    });

    if (arrayToCheckAgainst.includes(itemToCheck)) {
      return new ValidateResult(
        false,
        `${name} check: "${value}" is a duplicate.`,
        RETURN_CODES.DUPLICATE_VALUE,
      );
    }

    return Validators.successValidate(`${name} is unique`);
  },

  /**
   * Validates a schedule object.
   * @param {ScheduleValue|null} schedule - The schedule to validate.
   * @returns {ValidateResult} The result of the validation.
   */
  validateSchedule: schedule => {
    if (schedule === null) {
      return Validators.successValidate('Schedule skipped');
    }

    // validate start date YYYYMMDD format and that it's a valid date
    if (!/^\d{8}$/.test(schedule.date)) {
      return new ValidateResult(
        false,
        'Start date is invalid. It should be in YYYYMMDD format.',
        RETURN_CODES.INVALID_DATA,
      );
    }

    const startDate = parse(schedule.date, 'yyyyMMdd', new Date());

    if (!isValid(startDate)) {
      return new ValidateResult(false, 'Start date is invalid.', RETURN_CODES.INVALID_DATA);
    }

    // validate start time, should match HHMM where HH is 00-23 and MM is 00-59
    const timeRegex = /^([01]\d|2[0-3])([0-5]\d)$/;

    if (!timeRegex.test(schedule.time)) {
      return new ValidateResult(
        false,
        'Start time is invalid. It should be in HHMM format.',
        RETURN_CODES.INVALID_DATA,
      );
    }

    // validate recur option, 0,1,2 where 0 is one off, 1 is daily, 2 is selected days
    if (![0, 1, 2].includes(schedule.recur)) {
      return new ValidateResult(
        false,
        'Recur option is invalid. It should be 0 (one off), 1 (daily), or 2 (selected days).',
        RETURN_CODES.INVALID_DATA,
      );
    }

    // if recur option is 2, validate that at least one day is selected
    if (schedule.recur === 2) {
      // days should be: sunday = 1 monday 2 tuesday 4 wednesday 8 thursday 16 friday 32 saturday 64
      if (!Number.isInteger(schedule.days) || schedule.days < 1 || schedule.days > 127) {
        return new ValidateResult(
          false,
          'For selected days recur option, at least one day must be selected.',
          RETURN_CODES.INVALID_DATA,
        );
      }
    } else if (schedule.days !== 0) {
      // if not selecting specific days, days should be 0
      return new ValidateResult(
        false,
        'Days should be 0 when not selecting specific days of week.',
        RETURN_CODES.INVALID_DATA,
      );
    }

    return Validators.successValidate('Schedule valid');
  },
};
